import { Fragment, useEffect, useMemo, useState } from 'react';

import { SEARCHABLE_VENDOR_TAXONOMY_KEYS } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import {
  getVendorTaxonomyId,
  getVendorTaxonomyKeyFromId,
} from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { getVendorWordSingular } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { CtaType } from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import CalendarIcon from '@zola/zola-ui/src/assets/images/icons/calendar.svg';
import { CreamCircleIcon } from '@zola/zola-ui/src/components/CircleIcon/CircleIcon';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { SideDrawer } from '@zola/zola-ui/src/components/SideDrawer';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';

import { TrackingProductLocation, trackCtaClicked } from '~/util/trackingHelper';
import { getVendorIcon } from '~/util/vendorUtils';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { VendorDrawerEditForm } from './VendorDrawerEditForm';

import styles from './vendorDrawer.module.less';

interface VendorDrawerProps {
  displayDrawer: boolean;
  setShowDrawer: (key: boolean) => void;
}

interface VendorLinkProps {
  text: string;
  icon: string;
  url?: string | null;
  vendorKey: string;
  type?: number | null;
  setShowEditForm: (key: boolean) => void;
  setVendorTypeKey: (key: string) => void;
  setTaxonomyId: (key: number) => void;
  showArrow: boolean;
}

const trackCtaData = () => {
  trackCtaClicked({
    location: TrackingProductLocation.TOP_PICKS_DRAWER,
    section: 'TOP_PICKS_DRAWER',
    ctaId: 'Close',
    ctaType: CtaType.BUTTON,
  });
};

const VendorLink = ({
  text,
  icon,
  url,
  vendorKey,
  type,
  setShowEditForm,
  setVendorTypeKey,
  setTaxonomyId,
  showArrow,
}: VendorLinkProps) => {
  return (
    <div className={styles.vendorLink}>
      <CreamCircleIcon size={64} image={icon} />
      <LinkV2
        href={url || undefined}
        arrow={showArrow}
        onClick={() => {
          if (!url) {
            setShowEditForm(true);
            setVendorTypeKey(vendorKey);
            if (type) setTaxonomyId(type);
          }
        }}
      >
        {text}
      </LinkV2>
    </div>
  );
};

export const VendorDrawer = ({ displayDrawer, setShowDrawer }: VendorDrawerProps): JSX.Element => {
  const { vendorPreferences } = useSearchResultsPageContext();
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [vendorTypeKey, setVendorTypeKey] = useState<string>('wedding-general');
  const [taxonomyId, setTaxonomyId] = useState<number | null>(null);
  const [availableVendorTypes, setAvailableVendorTypes] = useState<Array<number>>([]);

  const closeDrawer = () => {
    setShowDrawer(false);
    trackCtaData();
  };

  const onboardedVendorTypes = useMemo(
    () => vendorPreferences.map((pref) => pref.taxonomyNodeId),
    [vendorPreferences]
  );
  useEffect(() => {
    if (onboardedVendorTypes.length > 0) {
      // for each type from the array of vendorCategoryIds that is not included
      // in onboardedVendorTypes, add the id to the availableVendorTypes array
      setAvailableVendorTypes(
        (SEARCHABLE_VENDOR_TAXONOMY_KEYS.map(getVendorTaxonomyId) as number[]).filter(
          (id) => !onboardedVendorTypes.includes(id)
        )
      );
    }
  }, [onboardedVendorTypes]);

  const generateVendorLinks = (vendorTypeArray: number[], setUrl: boolean) => {
    return vendorTypeArray.map((type: number) => {
      const vendorType = getVendorTaxonomyKeyFromId(type);
      if (vendorType) {
        const text = setUrl
          ? `Get ${getVendorWordSingular(vendorType)} Recs`
          : `Your ${getVendorWordSingular(vendorType)} Preferences`;
        const url = `/inspiration/onboard${`?entry=editPreferences&category=${vendorType}`}`;
        return (
          <VendorLink
            text={text}
            icon={getVendorIcon(vendorType)}
            vendorKey={vendorType}
            url={setUrl ? url : null}
            type={type}
            key={vendorType}
            setShowEditForm={setShowEditForm}
            setVendorTypeKey={setVendorTypeKey}
            setTaxonomyId={setTaxonomyId}
            showArrow={setUrl}
          />
        );
      }
      return null;
    });
  };

  const availableVendors = () => {
    if (availableVendorTypes.length) {
      return generateVendorLinks(availableVendorTypes, true);
    }
    return null;
  };

  const onboardedVendors = () => {
    if (onboardedVendorTypes.length) {
      return generateVendorLinks(onboardedVendorTypes, false);
    }
    return null;
  };

  const { isMobile } = useResponsiveDesign();

  return (
    <SideDrawer
      displayDrawer={displayDrawer}
      onClose={closeDrawer}
      width={isMobile ? '100vw' : '710px'}
      header=""
    >
      <div className={styles.container}>
        {showEditForm ? (
          <VendorDrawerEditForm
            setShowEditForm={setShowEditForm}
            taxonomyId={taxonomyId}
            vendorTypeKey={vendorTypeKey}
          />
        ) : (
          <div className={styles.content}>
            <h1 className={styles.header}>Your Preferences</h1>
            <div className={styles.dek}>
              Edit your wedding details and vendor preferences anytime.
            </div>
            <VendorLink
              text="Your Wedding Details"
              icon={CalendarIcon}
              vendorKey="wedding-general"
              type={null}
              key="wedding-general"
              setShowEditForm={setShowEditForm}
              setVendorTypeKey={setVendorTypeKey}
              setTaxonomyId={setTaxonomyId}
              showArrow={false}
            />
            {onboardedVendors()}
            <h2 className={styles.subheader}>Want recs for another type of vendor?</h2>
            {availableVendorTypes.length > 0 ? (
              <Fragment>
                <p className={styles.dek}>Tell us what you’re looking for.</p>
                <div className={styles.vendorOnboardingLinks}>{availableVendors()}</div>
              </Fragment>
            ) : (
              <p className={styles.dek}>Adjust your preferences above for new recs.</p>
            )}
          </div>
        )}
      </div>
    </SideDrawer>
  );
};
